/**
 * Interaction system types for DrawingJS.
 * Handles gesture recognition, state management, and event routing.
 */

import type { Point2D, Point3D } from "../types/types";

// ============== Gesture Types ==============

/**
 * Types of gestures recognized by the GestureRecognizer.
 */
export enum GestureType {
  /** No gesture in progress */
  None = "none",
  /** Quick touch and release */
  Tap = "tap",
  /** Two quick taps */
  DoubleTap = "double-tap",
  /** Hold for 500ms+ */
  LongPress = "long-press",
  /** Single pointer touch and move */
  Drag = "drag",
  /** Two-finger zoom gesture */
  Pinch = "pinch",
  /** Two-finger drag gesture */
  TwoFingerPan = "two-finger-pan",
}

/**
 * Pointer device types.
 */
export enum PointerType {
  Mouse = "mouse",
  Touch = "touch",
  Pen = "pen",
  Unknown = "unknown",
}

// ============== Interaction State ==============

/**
 * Current interaction state of the drawing system.
 * Determines how events are routed.
 */
export enum InteractionState {
  /** Ready for any action */
  Idle = "idle",
  /** Using a drawing tool */
  Drawing = "drawing",
  /** Making window/crossing selection */
  Selecting = "selecting",
  /** Moving/resizing selection */
  Transforming = "transforming",
  /** Pan/zoom navigation */
  Navigating = "navigating",
}

// ============== Gesture Event ==============

/**
 * A recognized gesture event with all relevant data.
 */
export interface GestureEvent {
  /** Type of gesture */
  type: GestureType;
  /** Pointer device type */
  pointerType: PointerType;
  /** Mouse button (0=left, 1=middle, 2=right) */
  button?: number;
  /** Screen position (relative to canvas) */
  screenPosition: Point2D;
  /** World position (converted from screen) */
  worldPosition?: Point3D;
  /** Original pointer event */
  originalEvent: PointerEvent;
  /** For pinch: distance between fingers */
  pinchDistance?: number;
  /** For pinch: previous distance between fingers */
  previousPinchDistance?: number;
  /** For pinch: center point between fingers */
  pinchCenter?: Point2D;
  /** For drag: delta from last position */
  delta?: Point2D;
  /** Timestamp */
  timestamp: number;
}

/**
 * Configuration for gesture recognition.
 */
export interface GestureConfig {
  /** Minimum distance to recognize as drag (pixels) */
  dragThreshold: number;
  /** Maximum time for tap gesture (ms) */
  tapMaxDuration: number;
  /** Maximum time between taps for double-tap (ms) */
  doubleTapMaxInterval: number;
  /** Minimum duration for long-press (ms) */
  longPressMinDuration: number;
}

/**
 * Default gesture configuration.
 */
export const DEFAULT_GESTURE_CONFIG: GestureConfig = {
  dragThreshold: 5,
  tapMaxDuration: 200,
  doubleTapMaxInterval: 300,
  longPressMinDuration: 500,
};

// ============== Point Types ==============

export type { Point2D, Point3D } from "../types/types";

// ============== Event Handlers ==============

/**
 * Handler function for gesture events.
 */
export type GestureHandler = (event: GestureEvent) => void;

/**
 * Interaction event types emitted by the InteractionManager.
 */
export enum InteractionEvent {
  /** State changed (idle -> drawing, etc.) */
  StateChanged = "state-changed",
  /** Gesture recognized */
  Gesture = "gesture",
  /** Navigation started */
  NavigationStart = "navigation-start",
  /** Navigation ended */
  NavigationEnd = "navigation-end",
}

/**
 * Payload for state change events.
 */
export interface StateChangePayload {
  previousState: InteractionState;
  newState: InteractionState;
}

// ============== Stylus Mode ==============

/**
 * Input mode for stylus-enabled devices.
 */
export enum InputMode {
  /** Normal mode: single finger draws, two fingers navigate */
  Normal = "normal",
  /** Stylus mode: finger always pans, stylus draws */
  StylusOnly = "stylus-only",
}

// ============== View Events ==============

/**
 * Event types for view changes.
 */
export enum ViewEvent {
  /** View transform changed (pan, zoom, rotate) */
  ViewChanged = "view-changed",
  /** View resize occurred */
  ViewResized = "view-resized",
}

/**
 * Payload for view changed events.
 */
export interface ViewChangedPayload {
  /** Type of change that triggered the event */
  changeType: "pan" | "zoom" | "pinch" | "resize" | "extents";
}
